import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CATEGORIES, HERO_IMAGE } from "@/lib/sessions";
import Logo from "@/components/Logo";
import CategoryIcon from "@/components/CategoryIcon";
import ExpirationReminder from "@/components/ExpirationReminder";
import { store } from "@/lib/storage";
import { ArrowUpRight, ChevronRight } from "lucide-react";

export default function Home() {
  const nav = useNavigate();
  const [greeting, setGreeting] = useState("Bonjour");

  // Nouveau visiteur : redirection directe vers le questionnaire de découverte.
  // On considère "nouveau visiteur" quand aucun profil n'existe et que la
  // découverte n'a jamais été proposée.
  useEffect(() => {
    const profile = store.getProfile();
    const alreadyPrompted = store.hasDiscoveryPrompt();
    const isNewVisitor = !alreadyPrompted && !(profile && profile.completed);
    if (isNewVisitor) {
      nav("/decouverte", { replace: true });
    }
  }, [nav]);

  useEffect(() => {
    const h = new Date().getHours();
    if (h < 6) setGreeting("Bonne nuit");
    else if (h < 12) setGreeting("Bonjour");
    else if (h < 18) setGreeting("Bel après-midi");
    else if (h < 22) setGreeting("Belle soirée");
    else setGreeting("Bonsoir");
  }, []);

  function goCategory(cat) {
    if (cat.mode === "baby") {
      nav("/bebe/ambiance");
    } else if (cat.mode === "ambient") {
      nav("/musique");
    } else {
      nav(`/suggestions?cat=${encodeURIComponent(cat.id)}`);
    }
  }

  return (
    <div className="animate-fade-in pt-4 pb-4">
      <ExpirationReminder />
      {/* En-tête compact avec logo */}
      <header className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[color:var(--ivoire)] flex items-center justify-center shadow-lg shadow-black/30">
            <Logo size={38} />
          </div>
          <div>
            <p className="font-serif text-lg text-[color:var(--ivoire)] leading-none">Soft Landing</p>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[color:var(--sauge-light)] mt-1">ASMR</p>
            <p className="font-serif italic text-[11px] text-[color:var(--ivoire-muted)] mt-1">Un moment pour retrouver le calme.</p>
          </div>
        </div>
        <p className="text-xs text-[color:var(--ivoire-muted)]">{greeting}</p>
      </header>

      {/* Hero — mène à la personnalisation générale */}
      <section className="mt-6" data-testid="hero-section">
        <button
          data-testid="hero-btn"
          onClick={() => nav("/personnaliser")}
          className="w-full text-left relative overflow-hidden rounded-3xl border border-[color:var(--border)] group"
          style={{ aspectRatio: "4 / 5" }}
        >
          <img
            src={HERO_IMAGE}
            alt=""
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080D1A] via-[#0D1B2A]/40 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--sauge-light)]">Découvrir</p>
            <h1 className="font-serif text-3xl sm:text-4xl leading-tight text-[color:var(--ivoire)] mt-2">
              Un moment
              <br />
              pour retrouver
              <br />
              <span className="italic text-[color:var(--sauge-light)]">le calme.</span>
            </h1>
            <p className="mt-3 text-sm text-[color:var(--ivoire-muted)] max-w-[22rem]">
              Répondez à trois questions pour obtenir des suggestions adaptées.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-[color:var(--sauge-light)] text-sm font-medium">
              Commencer <ArrowUpRight size={16} />
            </div>
          </div>
        </button>
      </section>

      {/* Menu de catégories — cartes horizontales avec image + icône + texte */}
      <section className="mt-8" data-testid="categories-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif text-2xl text-[color:var(--ivoire)]">Explorer</h2>
          <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">
            {CATEGORIES.length} thèmes
          </p>
        </div>

        <div className="space-y-3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              data-testid={`cat-${cat.id}`}
              onClick={() => goCategory(cat)}
              className="w-full text-left relative overflow-hidden rounded-2xl border border-[color:var(--border)] group h-24 flex items-center"
            >
              <img
                src={cat.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/95 via-[#0D1B2A]/60 to-[#0D1B2A]/30" />
              <div className="relative z-10 flex items-center gap-4 p-4 w-full">
                <CategoryIcon id={cat.id} size={44} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg text-[color:var(--ivoire)] leading-tight">{cat.label}</h3>
                  <p className="text-[11px] text-[color:var(--ivoire-muted)] mt-1 leading-snug line-clamp-2">
                    {cat.description}
                  </p>
                </div>
                <ChevronRight size={18} className="text-[color:var(--sauge-light)] opacity-70 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
